import { Ed25519KeyIdentity, Ed25519PublicKey, ECDSAKeyIdentity } from "@dfinity/identity";
import { Delegation, DelegationChain, isDelegationValid, DelegationIdentity } from '@dfinity/identity';
import { Secp256k1KeyIdentity, Secp256k1PublicKey } from '@dfinity/identity-secp256k1';
import { Actor, HttpAgent, AnonymousIdentity } from '@dfinity/agent';
import { Principal } from "@dfinity/principal";
import { toHexString } from '@dfinity/candid';
import { derivePath } from 'ed25519-hd-key';
import * as bip39 from 'bip39';
import HDKey from '@/libs/hdkey.js';
import Secp256k1 from 'secp256k1';
import { sha224 } from '@dfinity/principal/lib/esm/utils/sha224';
import { getCrc32 } from '@dfinity/principal/lib/esm/utils/getCrc';
import tweetnacl from 'tweetnacl';
import internal from "stream";

import ledger_idl from '@/candid/ledger.did.js';
import xtc_idl from '@/candid/xtc.did.js';

import axios from 'axios';
import { currentUnixTimestamp } from '@/libs/const';

const LEDGER_CANISTER_ID = 'ryjl3-tyaaa-aaaaa-aaaba-cai';// "rrkah-fqaaa-aaaaa-aaaaq-cai";

const blobFromUint8Array = uint8Array => {
  const blob = new Blob([uint8Array], { type: 'application/octet-stream' });
  return blob
}
const to32bits = num => {
  let b = new ArrayBuffer(4);
  new DataView(b).setUint32(0, num);
  return Array.from(new Uint8Array(b));
}



/***
 * @function principalToAccountIdentifier
 * 将主体和子账户编号转换为帐户标识符
 * 
 * @params    principal {string} Internet Computer上的主体
 * @params    num {number} 子账户编号
 * 
 * @returns   {string} 账户标识符的十六进制字符串
 * 
 * @example
 * const principal = "4vqrd-vqqrq-r6rab-6qw5c-xa26a-3aaq5-h5y5i-2qya5-5gq65-4yxf4-fqe";
 * const num = 0;
 * 
 * const accountIdentifier = principalToAccountIdentifier(principal, num);
 * console.log("Account Identifier:", accountIdentifier);
 * */
function principalToAccountIdentifier (principal: string, num: number = 0){
  console.log('typeof principal', typeof principal)
  const p = (typeof principal === 'string')?Principal.fromText(principal):principal
  const s = num
  const padding = new Uint8Array([0x0a, 0x61, 0x63, 0x63, 0x6f, 0x75, 0x6e, 0x74, 0x2d, 0x69, 0x64]); // "\x0Aaccount-id"

    // const padding = Buffer("\x0Aaccount-id");
  const array = new Uint8Array([
      ...padding,
      ...p.toUint8Array(),
      ...getSubAccountArray(s)
  ]);
  const hash = sha224(array);
  const checksum = to32bits(getCrc32(hash));
  const array2 = new Uint8Array([
      ...checksum,
      ...hash
  ]);
  return toHexString(array2);
};



const getSubAccountArray = (s) => {
  if (Array.isArray(s)){
    return s.concat(Array(32-s.length).fill(0));
  } else {
    //32 bit number only
    return Array(28).fill(0).concat(to32bits(s ? s : 0))
  }
};

////代码开始

/***
 * @function generateMnemonic
 * 生成助记词
 * 
 * @params    void  无
 * 
 * @returns   助记词
 * 
 * @example
 * 
 * const mnemonic = generateMnemonic();
 * console.log("Generated mnemonic:", mnemonic);
 * //"pistol maple duty lunch canyon critic critic pave volume feel kitten umbrella"
 * 
 * */
function generateMnemonic() {
  return bip39.generateMnemonic();
}

/***
 * @function mnemonicToSeed
 * 根据助记词生成种子
 * 函数接受一个字符串类型的助记词作为输入参数，并将返回一个 Buffer 类型的种子。
 * 
 * @params    mnemonic {string} 助记词
 * 
 * @returns   Buffer 类型的种子
 * 
 * @example
 * const mnemonic = "pistol maple duty lunch canyon critic critic pave volume feel kitten umbrella";
 * (async () => {
 *   const seed = await mnemonicToSeed(mnemonic);
 *   console.log("Generated seed:", seed.toString('hex'));
 * })();
 * 
 * */
function mnemonicToSeed(mnemonic) {
  const seed = bip39.mnemonicToSeedSync(mnemonic);
  return seed;
}

/***
 * @function createIdentity
 * 创建密钥对(identity)和对应算法
 * 
 * @params    algorithm {string} 密钥算法，可以是"ED25519"或"Secp256k1"
 * @params    seed {Buffer} 种子
 * @params    derivationPath {string} 密钥派生路径
 * 
 * @returns   { publicKey: Buffer, privateKey: Buffer } 密钥对
 * 
 * @example
 * const algorithm = "ED25519";
 * const seed = Buffer.from("a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2", "hex");
 * const derivationPath = "m/44'/0'/0'/0/0";
 * const identity = createIdentity(algorithm, seed, derivationPath);
 * console.log("Generated identity:", identity);
 * 
 * */
function createIdentity(algorithm, seed, derivationPath) {

  let publicKey, privateKey;
  const hdkey = HDKey.fromMasterSeed(seed);

  switch(algorithm){
    case 'ED25519':
      return _createIdentity_ED25519(seed, derivationPath);
      break;
    case 'Secp256k1':
    default:
      return _createIdentity_Secp256k1(seed, derivationPath);
  }

}

/***
 * @function generateIdentity
 * 直接创建一个身份
 * 
 * @params    void()
 * 
 * @returns   Identity
 * 
 * @example
 * const identity = generateIdentity(algorithm, seed, derivationPath);
 * console.log("Generated identity:", identity);
 * 
 * */
function generateIdentity(algorithm) {

  switch(algorithm){
    case 'ED25519':
      return ECDSAKeyIdentity.generate();
      break;
    case 'Secp256k1':
    default:
      return Secp256k1KeyIdentity.generate();
  }

}


/***
 * @function getBalance
 * 获取余额
 * 
 * @params    void()
 * 
 * @returns   Identity
 * 
 * @example
 * const balance = getBalance(identity);
 * console.log("balance:", balance);
 * 
 * */
async function getBalance(identity) {

  const agent = new HttpAgent({ identity, host:'https://ic0.app' });
  const ledger = Actor.createActor(ledger_idl, {
    agent,
    canisterId: Principal.fromText('ryjl3-tyaaa-aaaaa-aaaba-cai'),
  });
  const arg = { account: principalToAccountIdentifier((identity as any).getPrincipal().toText()) };
  console.log('arg', arg)
  const result:any = await ledger.account_balance_dfx(arg);
  console.log('ICP Balance:', result.e8s, Number(result.e8s) / 100000000);
  return Number(result.e8s) 

}

/***
 * @function getAccountBalance
 * 获取余额
 * 
 * @params    void()
 * 
 * @returns   balance
 * 
 * @example
 * const balance = getAccountBalance({account:'fcb91dddf38ca7dbad713222f98a368aac79ee27e4472ebd10b1eca4932fa39f'});
 * console.log("getUserBalance:", balance);
 * 
 * */
async function getAccountBalance({principal='', account='', type='icp'}) {

  if (!account){
    if (!principal){
      throw 'principal or account is required'
    }
    account = principalToAccountIdentifier(principal);
  }
  const identity = new AnonymousIdentity();

  const agent = new HttpAgent({ identity, host:'https://ic0.app' });
  let ledger;
  let result:any;

  switch(type){
    case 'xtc':
      ledger = Actor.createActor(xtc_idl, {
        agent,
        canisterId: Principal.fromText('aanaa-xaaaa-aaaah-aaeiq-cai'),
      });
      if (!principal){
        throw 'for xtc balance, principal is required'
      }
      result = await ledger.balanceOf(Principal.fromText(principal));
      // result = await ledger.balanceOf(Principal.fromText('aanaa-xaaaa-aaaah-aaeiq-cai'));
      // const balance:any = await xtc.balanceOf(Principal.fromText());
      console.log('Cycles Balance:', result);
      return Number(result);
      break;
    case 'icp':
    default:
      ledger = Actor.createActor(ledger_idl, {
        agent,
        canisterId: Principal.fromText('ryjl3-tyaaa-aaaaa-aaaba-cai'),
      });
      result = await ledger.account_balance_dfx({ account: account });
      return Number(result.e8s) 
  }


}



/***
 * @function getXtcBalance
 * 获取XTC余额
 * 
 * @params    void()
 * 
 * @returns   Identity
 * 
 * @example
 * const balance = getBalance(algorithm, seed, derivationPath);
 * console.log("balance:", balance);
 * 
 * */
async function getXtcBalance(identity) {

  const idlFactory = ({ IDL }) => {
    return IDL.Service({
      'balanceOf': IDL.Func([IDL.Principal], [IDL.Nat], ['query']),
    });
  };

  const agent = new HttpAgent({ identity, host:'https://ic0.app' });

  const xtc = Actor.createActor(idlFactory, {
    agent,
    canisterId: Principal.fromText('aanaa-xaaaa-aaaah-aaeiq-cai'),
    host:'https://ic0.app'
  } as any);

  const balance:any = await xtc.balanceOf((identity as any).getPrincipal());
  // const balance:any = await xtc.balanceOf(Principal.fromText('aanaa-xaaaa-aaaah-aaeiq-cai'));
  console.log('Cycles Balance:', balance);
  return Number(balance);

}







function _createIdentity_Secp256k1(seed, derivationPath) {

  if (!derivationPath){
    derivationPath = "m/44'/223'/0'/0";
  } 
  const masterKey = HDKey.fromMasterSeed(seed);
  const { privateKey } = masterKey.derive(`${derivationPath}/0`);
  const publicKey = Secp256k1.publicKeyCreate(privateKey, false);
  const identity = Secp256k1KeyIdentity.fromSecretKey(privateKey);
  return identity

}


function _createIdentity_ED25519(seed, derivationPath) {

  if (!derivationPath){
    derivationPath = "m/44'/223'/0'/0'";
  } 
  const { key } = derivePath(derivationPath, seed.toString('hex'), 0);
  const keyPair = tweetnacl.sign.keyPair.fromSeed(key);
  const rawPrivateKey = toHexString(keyPair.secretKey);
  const rawPublicKey = toHexString(keyPair.publicKey);
  const identity = Ed25519KeyIdentity.fromSecretKey(keyPair.secretKey);
  return identity

}


/***
 * @function exportPem
 * 导出密钥对的PEM格式字符串
 * 
 * @params    identity {any} 包含 publicKey 和 privateKey 的对象
 * @params    algorithm {string} 密钥算法，可以是"ED25519"或"Secp256k1"
 * 
 * @returns   { publicKeyPem: string, privateKeyPem: string } PEM格式的密钥对
 * 
 * @example
 * const algorithm = "ED25519";
 * const identity = ...
 * 
 * exportPem(identity, algorithm)
 * 
 * 
 * */

function exportPem(identity, algorithm){

  switch(algorithm){
    case 'ED25519':
      return _exportPem_ED25519(identity);
      break;
    case 'Secp256k1':
    default:
      return _exportPem_Secp256k1(identity);
  }

}


function _exportPem_Secp256k1(identity) {

  const PEM_BEGIN = `-----BEGIN EC PARAMETERS-----
BgUrgQQACg==
-----END EC PARAMETERS-----
-----BEGIN EC PRIVATE KEY-----`;
  const PEM_END = '-----END EC PRIVATE KEY-----';
  const PRIV_KEY_INIT = '30740201010420';
  const KEY_SEPARATOR = 'a00706052b8104000aa144034200';
  const keyPair:any = identity.getKeyPair();
  const rawPrivateKey = toHexString(keyPair.secretKey);
  const rawPublicKey = toHexString(keyPair.publicKey.toRaw());

  const pem = `${PEM_BEGIN}\n${Buffer.from(
    `${PRIV_KEY_INIT}${rawPrivateKey}${KEY_SEPARATOR}${rawPublicKey}`,
    'hex'
  ).toString('base64')}\n${PEM_END}`;

  return pem;

}


function _exportPem_ED25519(identity) {

  const PEM_BEGIN = `-----BEGIN EC PARAMETERS-----
  BgUrgQQACg==
  -----END EC PARAMETERS-----
  -----BEGIN EC PRIVATE KEY-----`;
  const PEM_END = '-----END EC PRIVATE KEY-----';
  const PRIV_KEY_INIT = '30740201010420';
  const KEY_SEPARATOR = 'a00706052b8104000aa144034200';

  const keyPair:any = identity.getKeyPair();
  const rawPrivateKey = toHexString(keyPair.secretKey);
  const rawPublicKey = toHexString(keyPair.publicKey.toRaw());

  const pem = `${PEM_BEGIN}\n${Buffer.from(
      `${PRIV_KEY_INIT}${rawPrivateKey}${KEY_SEPARATOR}${rawPublicKey}`,
      'hex'
    ).toString('base64')}\n${PEM_END}`;

  return pem;

}



/***
 * @function importPem
 * 导入密钥对的PEM格式字符串
 * 
 * @params    pem {any} pem 格式字符串
 * @params    algorithm {string} 密钥算法，可以是"ED25519"或"Secp256k1"
 * 
 * @returns   identity
 * 
 * @example
 * const algorithm = "ED25519";
 * const pem = ...
 * 
 * importPem(pem, algorithm)
 * 
 * 
 * */

function importPem(pem, algorithm){

  switch(algorithm){
    case 'ED25519':
      return _importPem_ED25519(pem);
      break;
    case 'Secp256k1':
    default:
      return _importPem_Secp256k1(pem);
  }

}


function _importPem_Secp256k1(pem) {

  const trimedPem = pem
  .replace(/(-{5}.*-{5})/g, '')
  .replace('\n', '')
  // Sepk256k1 keys
  .replace('BgUrgQQACg==', '')
  .trim();

  const SEC256k1_KEY_INIT = '30740201010420';
  const SEC256k1_KEY_SEPARATOR = 'a00706052b8104000aa144034200';
  const SEC256k1_OID = '06052b8104000a'

  const raw = toHexString(Buffer.from(trimedPem, 'base64'))

  if (!raw.includes(SEC256k1_OID)) {
    return undefined;
  }

  const trimRaw = raw
    .replace(SEC256k1_KEY_INIT, '')
    .replace(SEC256k1_KEY_SEPARATOR, '')

  try {
    const key = new Uint8Array(Buffer.from(trimRaw.substring(0, 64), 'hex'));
    const identity = Secp256k1KeyIdentity.fromSecretKey(key);
    return identity;
  } catch {
    return undefined;
  }

}


function _importPem_ED25519(pem) {
  
  const trimedPem = pem
  .replace(/(-{5}.*-{5})/g, '')
  .replace('\n', '')
  // Sepk256k1 keys
  .replace('BgUrgQQACg==', '')
  .trim();

  const ED25519_KEY_INIT = '3053020101300506032b657004220420';
  const ED25519_KEY_SEPARATOR = 'a123032100';
  const ED25519_OID = '06032b6570';

  const raw = toHexString(Buffer.from(trimedPem, 'base64'))

  if (!raw.substring(0, 24).includes(ED25519_OID)) {
    return undefined;
  }

  const trimRaw = raw
    .replace(ED25519_KEY_INIT, '')
    .replace(ED25519_KEY_SEPARATOR, '');

  try {
    const key = new Uint8Array(Buffer.from(trimRaw.substring(0, 128), 'hex'));
    const identity = Ed25519KeyIdentity.fromSecretKey(key);
    return identity;
  } catch(e) {
    console.log('err', e)
    return undefined
  }

}
/***
 * @function identityToPrincipal
 * 将密钥对转换为 Principal 上的主体
 * @params    identity {any} 包含 publicKey 和 privateKey 的对象
 * @returns   {Principal} Principal上的主体
 * 
 * @example
 * const identity = ...
 * 
 * const principal = identityToPrincipal(identity);
 * console.log("Principal:", principal.toText());
 * // "4vqrd-vqqrq-r6rab-6qw5c-xa26a-3aaq5-h5y5i-2qya5-5gq65-4yxf4-fqe"
 * 
 * */
function identityToPrincipal(identity) {

  const keyPair:any = identity.getKeyPair();
  const principal = Principal.selfAuthenticating(keyPair.publicKey.toDer());
  return principal;

}


/**
 * @function transferIcp
 * 在 Internet Computer 上将 ICP 代币从一个账户转移到另一个账户。
 *
 * @param fromIdentity - 发送者的身份。此身份将用于对交易进行签名。
 * @param toPrincipal - 收款人账户的 Principal。
 * @param amount - 要转移的 ICP 代币数量，用 BigInt 表示。
 * @param memo - 可选的 32 位无符号整数，与交易一起作为备注包含。默认为 0。
 * 
 * @example
 * 
    let fromIdentity = <identity>;
    let to = 'someaddress'
    //转账金额 0.01 
    let amount = 1000000n
    let memo = 0
    transferIcp(fromIdentity, 0, to, amount, memo)
 * 
 * @returns 一个 Promise，解析为一个布尔值，表示转账的成功或失败。
 */

async function transferIcp(
  fromIdentity: import("@dfinity/agent").Identity,
  fromSubAccount: number,
  to: string,
  amount: bigint,
  memo: number = 0
) {
  const ledgerCanisterId = LEDGER_CANISTER_ID;
  const agent = new HttpAgent({ host: "https://ic0.app", identity: fromIdentity });
  const ledger = Actor.createActor(ledger_idl, {
    agent,
    canisterId: Principal.fromText(ledgerCanisterId),
  });

  const fee: bigint = BigInt(10000); // Set the transaction fee, adjust as needed

  const sendArgs = {
    to: to,
    fee: { e8s: fee },
    memo: memo ? Number(BigInt(memo)) : 0,
    from_subaccount: [getSubAccountArray(fromSubAccount ?? 0)], // Use the provided subaccount or default to 0
    created_at_time: [], // Use the default timestamp
    amount: { e8s: amount },
  };

  console.log('sendArgs', sendArgs);
  const result = await ledger.send_dfx(sendArgs);
  console.log("Transfer result:", result);

  return result;
}


/**
 * 创建委托链 JSON
 *
 * @param {Identity} alice - Alice 的身份对象
 * @param {PublicKey} bobPublicKey - Bob 的公钥
 * @param {string} expiredDate - 委托链的过期日期，默认为 '2100-01-01'
 * @returns {Promise<string>} 返回委托链 JSON
 * @example
 * const alice = await ECDSAKeyIdentity.generate();
 * const bob = await ECDSAKeyIdentity.generate();
 * const bobPublicKey = bob.getPublicKey();
 * const delegationChainJson = await createDelegationChainJSON(alice, bobPublicKey);
 */
async function createDelegationChainJSON(alice, bobPublicKey, expiredDate = '2100-01-01') {
  const delegationChain = await DelegationChain.create(
    alice, bobPublicKey, new Date(expiredDate),
  );

  const chainJson = delegationChain.toJSON();
  return chainJson;
}

/**
 * 从委托链 JSON 获取委托身份
 *
 * @param {Identity} bob - Bob 的身份对象
 * @param {string} chainJson - 委托链 JSON
 * @returns {Promise<DelegationIdentity>} 返回委托身份
 * @example
 * const bob = await ECDSAKeyIdentity.generate();
 * const delegatedIdentity = await getDelegatedIdentity(bob, delegationChainJson);
 */
async function getDelegatedIdentity(bob, chainJson) {
  const chain = DelegationChain.fromJSON(chainJson);
  const bobDelegationIdentity = DelegationIdentity.fromDelegation(bob, chain);
  return bobDelegationIdentity;
}

/**
 * 创建委托身份
 *
 * @param {Identity} alice - Alice 的身份对象
 * @param {PublicKey} bobPublicKey - Bob 的公钥
 * @param {string} expiredDate - 委托链的过期日期，默认为 '2100-01-01'
 * @returns {Promise<DelegationIdentity>} 返回委托身份
 * @example
 * const alice = await ECDSAKeyIdentity.generate();
 * const bob = await ECDSAKeyIdentity.generate();
 * const bobPublicKey = bob.getPublicKey();
 * const delegationIdentity = await createDelegationIdentity(alice, bobPublicKey);
 */
async function createDelegationIdentity(alice, bobPublicKey, expiredDate = '2100-01-01') {
  const delegationChain = await DelegationChain.create(
    alice, bobPublicKey, new Date(expiredDate),
  );
  const chainJson = delegationChain.toJSON();
  const bob = await ECDSAKeyIdentity.generate();
  const bobDelegationIdentity = await getDelegatedIdentity(bob, chainJson);
  return bobDelegationIdentity;
}

async function getIcpRate(){
  
  const ctime = currentUnixTimestamp();
  if (localStorage.getItem('icpPrice_expired')){
    if (ctime < Number(localStorage.getItem('icpPrice_expired')) ){
      if (localStorage.getItem('icpPrice')){
        return localStorage.getItem('icpPrice') 
      }
    }
  }

  const result = await axios.get('https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=internet-computer&order=market_cap_desc&per_page=100&page=1&sparkline=false')  
  const icpPrice = result.data[0].current_price
  localStorage.setItem('icpPrice', icpPrice)
  localStorage.setItem('icpPrice_expired', (Number(ctime) + Number(300000)).toString())
  return icpPrice
}

export {
  principalToAccountIdentifier,
  generateMnemonic,
  mnemonicToSeed,
  createIdentity,
  exportPem,
  importPem,
  identityToPrincipal,
  generateIdentity,
  getBalance,
  getXtcBalance,
  transferIcp,
  createDelegationChainJSON,
  getDelegatedIdentity,
  createDelegationIdentity,
  getSubAccountArray,
  getAccountBalance,
  getIcpRate
};



// async function test(){

//   // const delegation = {"delegations":[{"delegation":{"expiration":"17545b0786cd6d75","pubkey":"3059301306072a8648ce3d020106082a8648ce3d0301070342000407972d7bcc36f5db2855f537d94cc6d11385451df4e8d024525446cc7ca9cd805d2ccba8985c3ada726e67b2407274bf3869052ee8bfc753a3073f89de6116de"},"signature":"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"}],"publicKey":"303c300c060a2b0601040183b8430102032c000a00000000000000070101d3079726c3a9eb6c714f8e7b50d2fdaae2ead91d09d0e774bddac84847e360ec"}
  

//   let identity = Ed25519KeyIdentity.fromJSON('["302a300506032b65700321007f36922bf798eba2fda13489fcea95e3661a75a792b7a89ab356e0b6c6f5c38e","a42f7851f1afade31f5313ca320a232ee9374c48c54d58e9de0df0c88833cd617f36922bf798eba2fda13489fcea95e3661a75a792b7a89ab356e0b6c6f5c38e"]')
//   console.log('identity', identity)
//   console.log('identity', identity.getPrincipal())
//   console.log('identity', identity.getPrincipal().toText())

// }

// test();


// async function test_main() {

//     const alice = await ECDSAKeyIdentity.generate();
//     const bob = await ECDSAKeyIdentity.generate();
//     const bobPublicKey:any = bob.getPublicKey()

//     console.log('alice', alice.getPrincipal().toText());
//     console.log('bob', bob.getPrincipal().toText());

//     let delegationChainJson = await createDelegationChainJSON(alice, bobPublicKey)
//     console.log('delegationChainJson', delegationChainJson);

//     let delegatedIdentity0 = await getDelegatedIdentity(bob, delegationChainJson)
//     console.log('delegatedIdentity0', delegatedIdentity0.getPrincipal().toText());

//     let delegationIdentity = await createDelegationIdentity(alice, bobPublicKey)
//     console.log('delegationIdentity', delegationIdentity.getPrincipal().toText());

// }

// test_main().catch((error) => {
//   console.error("Error:", error);
// });

// async function test_main() {

//   // 使用 Alice 和 Bob 的身份替换以下示例
//   const alice = await ECDSAKeyIdentity.generate();
//   const bob = await ECDSAKeyIdentity.generate();
//   const publicKey:any = bob.getPublicKey()

//   console.log('alice.getPrincipal()', alice.getPrincipal().toText())
//   console.log('bob.getPrincipal()', bob.getPrincipal().toText())

//   const delegationChain:any = await DelegationChain.create(
//     alice, publicKey, new Date('2100-01-01'),
//   );
//   console.log('delegationChain', delegationChain)

//   const chainJson = delegationChain.toJSON();
//   console.log('chainJson', chainJson)


//   const chain = DelegationChain.fromJSON(chainJson)
//   console.log('chain', chain)


//   const bobDelegationIdentity = DelegationIdentity.fromDelegation(bob, chain /*delegationChain*/);
//   console.log('bobDelegationIdentity.getPrincipal()', bobDelegationIdentity.getPrincipal().toText())

//   const agent = new HttpAgent({ host: "https://ic0.app", identity: bobDelegationIdentity });
//   const webapp_id = 'k6l4o-oyaaa-aaaag-abfsq-cai';
//   const webapp_idl = ({ IDL }) => {
//     return IDL.Service({ whoami: IDL.Func([], [IDL.Principal], ["query"]) });
//   };
//   const webapp = Actor.createActor(webapp_idl, {
//     agent,
//     canisterId: webapp_id,
//     host:'https://ic0.app'
//   } as any);
//   const principal:any = await webapp.whoami();
//   console.log("Whoami result:", principal.toText(), principal);

// }

// // test_main().catch((error) => {
// //   console.error("Error:", error);
// // });
// async function testSerialize(){

//   const bob = await ECDSAKeyIdentity.generate();
//   console.log('bob.getKeyPair()', bob.getKeyPair())
//   localStorage.KEY_STORAGE_KEY = bob.getKeyPair()

// }

// testSerialize().catch((error) => {
//   console.error("Error:", error);
// });
